import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import SectionTitle from 'components/section-title'
import SectionEmptyState from 'components/section-empty-state'
import { DateTime } from 'luxon'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import CalendarTodayOutlinedIcon from 'components/material-symbol/icons/CalendarToday'
import useIconAccessor from 'components/lesson-type-category/icons'
import SessionDetailsDialog from 'components/session-details-dialog'
import EventDetailsDialog from 'pages/pga-coach/bookings/events/event-details-dialog'
import { onCancellation, onPayment, onReschedule } from 'lib/utils'
import { RefetchContext } from 'lib/utils/refetchContext'
import { parseEvent } from 'utils/eventUtils'
import { isJuniorAgeGroupEvent } from 'utils/groupEventUtils'
import { UpcomingListSkeleton } from './loading-skeleton'
import useAddBookingAction from './use-add-booking-action'
import useStyles from './styles'

const LESSON_LIMIT = 4

// The schedule is a union that can also carry blocked time, which has no
// details dialog and is not a lesson.
const LESSON_TYPENAMES = ['Session', 'GroupEvent']

const getDurationMinutes = (startDateTime, endDateTime) =>
  Math.round(
    DateTime.fromISO(endDateTime)
      .diff(DateTime.fromISO(startDateTime), 'minutes')
      .toObject().minutes,
  )

const formatLessonDate = start =>
  start.hasSame(DateTime.now().setZone(start.zone), 'day')
    ? 'Today'
    : start.toFormat('ccc, LLL d')

const getLessonRow = (booking, iconAccessor) => {
  const start = DateTime.fromISO(booking.startDateTime).setZone(
    booking.timezone,
  )

  if (booking.__typename === 'Session') {
    const student = booking.enrollments[0]?.student
    const category = booking.lessonType?.category
    const categoryMeta = category ? iconAccessor[category] : null
    const fullName =
      student && `${student.firstName} ${student.lastName}`.trim()
    const duration =
      booking.lessonType?.durationInMinutes ||
      getDurationMinutes(booking.startDateTime, booking.endDateTime)

    return {
      key: `${booking.id}-${booking.startDateTime}`,
      booking,
      name: fullName || booking.title,
      meta: `${categoryMeta?.text || booking.title} · ${duration}m`,
      date: formatLessonDate(start),
      time: start.toFormat('h:mm a'),
    }
  }

  const duration = getDurationMinutes(
    booking.startDateTime,
    booking.endDateTime,
  )
  const isJunior = isJuniorAgeGroupEvent(booking.tags || [])

  return {
    key: `${booking.id}-${booking.startDateTime}`,
    booking,
    name: booking.title,
    meta: `${isJunior ? 'Junior Group' : 'Group Event'} · ${duration}m`,
    date: formatLessonDate(start),
    time: start.toFormat('h:mm a'),
  }
}

const noop = () => {}

const UpcomingLessonRow = ({ row }) => {
  const classes = useStyles()
  const refresh = useContext(RefetchContext) || noop
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const { booking } = row

  const open = () => {
    setHasOpened(true)
    setIsOpen(true)
  }

  return (
    <>
      <Box
        className={`${classes.row} ${classes.upcomingRow}`}
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            open()
          }
        }}
      >
        <Box className={classes.rowMain}>
          <Typography component="span" className={classes.rowName}>
            {row.name}
          </Typography>
          <Typography component="div" className={classes.rowMeta}>
            {row.meta}
          </Typography>
        </Box>
        <Box className={classes.rowTrailing}>
          <Box className={classes.rowSchedule}>
            <Typography component="div" className={classes.rowDate}>
              {row.date}
            </Typography>
            <Typography component="div" className={classes.rowTime}>
              {row.time}
            </Typography>
          </Box>
        </Box>
      </Box>

      {hasOpened && booking.__typename === 'Session' ? (
        <SessionDetailsDialog
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          booking={booking}
          onReschedule={() => onReschedule(refresh)}
          onPayment={() => onPayment(refresh)}
          onCancellation={() => onCancellation(refresh)}
        />
      ) : hasOpened && booking.__typename === 'GroupEvent' ? (
        <EventDetailsDialog
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          event={parseEvent(booking)}
        />
      ) : null}
    </>
  )
}

UpcomingLessonRow.propTypes = {
  row: PropTypes.shape({
    booking: PropTypes.object.isRequired,
    name: PropTypes.string,
    meta: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
  }).isRequired,
}

const UpcomingEmptyState = () => {
  const classes = useStyles()
  const addBooking = useAddBookingAction({
    formLabel: 'dashboard-upcoming-lessons',
  })

  return (
    <Box className={classes.emptyFill}>
      <SectionEmptyState
        compact
        bordered={false}
        Icon={CalendarTodayOutlinedIcon}
        title="No upcoming lessons"
        caption="Bookings you add will show up here."
        action={
          addBooking ? (
            <Button
              variant="contained"
              color="primary"
              component={addBooking.href ? 'a' : undefined}
              href={addBooking.href}
              onClick={addBooking.onClick}
            >
              Add booking
            </Button>
          ) : null
        }
      />
    </Box>
  )
}

const UpcomingLessons = ({ lessons, loading }) => {
  const classes = useStyles()
  const iconAccessor = useIconAccessor()
  const rows = lessons
    .filter(lesson => LESSON_TYPENAMES.includes(lesson.__typename))
    .slice(0, LESSON_LIMIT)
    .map(lesson => getLessonRow(lesson, iconAccessor))

  return (
    <Box className={`${classes.card} ${classes.cardColumn}`}>
      <Box className={`${classes.cardHeader} ${classes.cardHeaderRoomy}`}>
        <Box className={classes.cardHeaderLeft}>
          <Box className={`${classes.iconCircle} ${classes.iconCircleLarge}`}>
            <EventAvailableOutlinedIcon />
          </Box>
          <Box className={classes.cardHeading}>
            <SectionTitle className={classes.cardTitle}>
              Upcoming lessons
            </SectionTitle>
            <Typography className={classes.cardSubtitle}>
              Your next bookings
            </Typography>
          </Box>
        </Box>
        <Button
          component={RouterLink}
          to="/pga-coach/bookings/schedule"
          className={classes.cardAction}
        >
          View schedule
        </Button>
      </Box>

      {loading ? (
        <UpcomingListSkeleton />
      ) : rows.length > 0 ? (
        <Box className={classes.list}>
          {rows.map(row => (
            <UpcomingLessonRow key={row.key} row={row} />
          ))}
        </Box>
      ) : (
        <UpcomingEmptyState />
      )}
    </Box>
  )
}

export default UpcomingLessons
